import { useLayoutEffect, useRef } from 'react';

import { maskedDigits, maskedPrefix, pastedEntry, toEditableDigits } from '../proxyId';

export interface ProxyIdFieldProps {
  readonly id: string;
  readonly label: string;
  /**
   * What the candidate entered: **the digits after the prefix**, plain and with no separator.
   */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /**
   * The configured prefix, read from the api [03-us-kpc-1e; 0423]. When it is set, the prefix is shown and
   * cannot be changed, and the input takes digits only, 16 − p of them, grouped in fours by position.
   * **Empty is the fallback** when the read does not answer [AC-7]: nothing is fixed, and the input takes
   * 16 digits, digits only, grouped the same way.
   */
  readonly prefix?: string;
  /**
   * Whether to suppress paste, drag and autofill.
   *
   * Only the CONFIRMATION field sets it. Blocking the first field too would stop somebody pasting an id
   * their recruiter texted them, which is a legitimate way to get it onto a kiosk; blocking the second is
   * what makes the double entry mean anything, because pasting the same wrong value twice confirms nothing.
   */
  readonly suppressPaste?: boolean;
  /**
   * Called when a paste or a drop is REFUSED by the mask [03-us-kpc-1e AC-6] — a value that does not fit is
   * not entered, and the screen shows the format message. Never called on the confirmation field, which
   * refuses every paste and drop before the mask sees it [0037].
   */
  readonly onRefused?: () => void;
  /**
   * The id of the element carrying the message that explains why this value was refused. **Present means
   * the field is in error**, absent means it is not.
   *
   * One prop rather than an `invalid` boolean beside an `errorId`, because the two could only ever be set
   * together: the screen renders ONE `role="alert"` for both fields — the double entry's failures are about
   * the pair, not about one box — so what this field needs is a pointer to it, and `aria-invalid` follows
   * from having one. `Field` takes the message itself instead; that component renders its own.
   */
  readonly errorId?: string;
  /**
   * A handle on the input, so the screen can put focus here when the camera layer closes with a value.
   *
   * A plain prop rather than `forwardRef`: React 19 passes `ref` like any other prop to a function
   * component, and the repository has no wrapper to keep compatible. It is the INPUT rather than the
   * wrapper — the caller wants the control a candidate types into, not the div around it.
   */
  readonly ref?: React.Ref<HTMLInputElement>;
}

/**
 * A pay-card proxy id field [KPC-1; Figma · `12021:41973`].
 *
 * **Local to this feature rather than in `src/components/`**, and deliberately not an extension of `Field`.
 * It has one consumer, and the convention puts a one-consumer component with its consumer — but the real
 * reason is that its behaviour is a security control, not a style: paste suppression, no autofill, no
 * spellcheck, and a value that never leaves the component that collects it. Folding that into the shared
 * text field would offer it to every screen, including ones where suppressing paste is only an annoyance.
 */
export function ProxyIdField({
  id,
  label,
  value,
  onChange,
  prefix = '',
  suppressPaste = false,
  onRefused,
  errorId,
  ref,
}: ProxyIdFieldProps) {
  const input = useRef<HTMLInputElement | null>(null);
  /*
   * Where the caret goes after a masked edit, counted in DIGITS rather than characters. Re-grouping changes
   * the text, and a controlled input whose text changes puts the caret at the end — so a candidate fixing
   * one digit in the middle would type the next one at the end without noticing.
   */
  const caretDigits = useRef<number | null>(null);
  const shown = maskedDigits(prefix, value);

  useLayoutEffect(() => {
    const element = input.current;
    const digits = caretDigits.current;
    caretDigits.current = null;
    if (element === null || digits === null || document.activeElement !== element) return;
    const caret = caretAfterDigits(element.value, digits);
    element.setSelectionRange(caret, caret);
  });

  /*
   * `onPaste`/`onDrop` prevented rather than the value filtered afterwards: filtering would let the paste
   * land and then silently mangle it, which reads as a broken keyboard. The warning above the fields tells
   * the candidate this happens and why, so the refusal is explained rather than mysterious.
   */
  const suppressors = suppressPaste
    ? {
        onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
          event.preventDefault();
        },
        onDrop: (event: React.DragEvent<HTMLInputElement>) => {
          event.preventDefault();
        },
      }
    : /*
        Where this field ACCEPTS a paste or a drop, the value goes through the mask as a whole
        [03-us-kpc-1e AC-6]. The mask neither enables nor suppresses a paste [0037]: this branch exists only
        on a field that already accepted one. A whole id is entered without its prefix repeated; a value
        that does not fit is not entered at all, never reshaped.
      */
      {
        onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
          const entry = pastedEntry(event.clipboardData.getData('text'), prefix);
          // Digits that fit the editable positions land where the caret is, exactly as typing them would.
          if (entry.kind === 'asTyped') return;
          event.preventDefault();
          if (entry.kind === 'whole') onChange(entry.digits);
          else onRefused?.();
        },
        onDrop: (event: React.DragEvent<HTMLInputElement>) => {
          // Always handled here: a drop has no caret to land at, so digits that fit follow what is held.
          event.preventDefault();
          const entry = pastedEntry(event.dataTransfer.getData('text'), prefix);
          if (entry.kind === 'whole') onChange(entry.digits);
          else if (entry.kind === 'asTyped')
            onChange(toEditableDigits(value + entry.digits, prefix));
          else onRefused?.();
        },
      };

  const box =
    'w-full rounded-[var(--radius-xs)] bg-[var(--card)] p-2 text-base leading-6 text-[var(--foreground)] shadow-[inset_0_0_0_1px_var(--border-subtle)]';
  const prefixId = `${id}-prefix`;
  /*
    The refusal reaches a screen reader, not only the eye (§ 9): `aria-invalid` says the value was
    rejected and `aria-describedby` points at the sentence that says why. Without them the message is
    an unrelated paragraph somewhere below two boxes that look and announce exactly as they did
    before — and a candidate at a kiosk gets no second explanation. In the masked form it also points at
    the prefix, which sits beside the input rather than in its value, so it is announced with the field.
  */
  const describedBy =
    [prefix === '' ? undefined : prefixId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex w-full flex-col items-start gap-1">
      <label className="w-full text-sm font-bold leading-5 text-[var(--foreground)]" htmlFor={id}>
        {label}
      </label>
      {/*
        The masked form [0423; 03-us-kpc-1e]: the prefix is TEXT beside the input, not part of its value,
        so nothing the candidate does at the keyboard — a backspace, a select-all, a caret placed in front
        of it — can reach it. "Nothing can change it" is then a matter of structure, not of key handling.

        The box is drawn on the wrapper in BOTH forms, so the two read as one field and the focus indicator
        follows it — and so the input is the same element before and after the prefix read lands: a field
        that remounted under a candidate's finger would drop their focus.
      */}
      <div
        className={`flex items-center ${box} focus-within:outline focus-within:outline-[3px] focus-within:outline-offset-2 focus-within:outline-[var(--primary)]`}
      >
        {prefix === '' ? null : (
          <>
            {/*
              Two spans for one prefix. The visible one is grouped, so a long prefix reads `1234 - 56`;
              assistive technology gets the PLAIN prefix instead [AC-2; eval-kpc-1e E2], the value the api
              returned, with no separator for a screen reader to say. Digits only — no new word to translate.
            */}
            <span aria-hidden className="shrink-0 whitespace-pre">
              {maskedPrefix(prefix)}
            </span>
            <span className="sr-only" id={prefixId}>
              {prefix}
            </span>
          </>
        )}
        <input
          aria-describedby={describedBy}
          aria-invalid={errorId === undefined ? undefined : true}
          // No autofill and no spellcheck: a browser offering to remember a pay-card id, or underlining it as
          // a misspelling, both treat a financial identifier as ordinary text.
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent p-0 text-base leading-6 text-[var(--foreground)] outline-none"
          data-1p-ignore
          id={id}
          // `text`, not `password`: an unattended kiosk has nobody shoulder-surfing the candidate's own card,
          // and masking a value they must type twice correctly makes an error they cannot see.
          inputMode="numeric"
          onChange={(event) => {
            const element = event.target;
            const typed = element.value;
            const caret = element.selectionStart ?? typed.length;
            const digitsBeforeCaret = typed.slice(0, caret).replace(/\D/g, '').length;
            const next = toEditableDigits(typed, prefix);
            if (next === value) {
              /*
                An edit that changes no digit — a letter, a space, a separator deleted, a digit past the
                last position — re-renders nothing, so the layout effect never runs. React writes the old
                text back once this handler returns, and that moves the caret to the end; put it back after.
              */
              queueMicrotask(() => {
                const at = caretAfterDigits(element.value, digitsBeforeCaret);
                element.setSelectionRange(at, at);
              });
              return;
            }
            caretDigits.current = digitsBeforeCaret;
            onChange(next);
          }}
          ref={(element) => {
            input.current = element;
            assignRef(ref, element);
          }}
          spellCheck={false}
          type="text"
          value={shown}
          {...suppressors}
        />
      </div>
    </div>
  );
}

/**
 * The text position just after the `digits`-th digit — where the caret belongs once re-grouping has moved
 * the separators around it. Past the last digit it is the end.
 */
function caretAfterDigits(shown: string, digits: number): number {
  if (digits === 0) return 0;
  let seen = 0;
  for (let index = 0; index < shown.length; index += 1) {
    if (/\d/.test(shown.charAt(index))) seen += 1;
    if (seen === digits) return index + 1;
  }
  return shown.length;
}

/** Hands the input to the caller's ref as well as this component's own. */
function assignRef(
  ref: React.Ref<HTMLInputElement> | undefined,
  element: HTMLInputElement | null,
): void {
  if (typeof ref === 'function') {
    ref(element);
    return;
  }
  if (ref !== null && ref !== undefined) ref.current = element;
}
